import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  Button,
  ButtonBase,
  IconButton,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useLocation, useNavigate } from "react-router";
import { useAuth } from "../../auth/AuthProvider";
import { colors, fonts } from "../../styles/theme";
import ProfileMenu from "../common/overlays/ProfileMenu";
import ProjectSwitcher from "../common/overlays/ProjectSwitcher";
import { useHeader } from "../../contexts/HeaderContext";
import SeitenTitel from "./SeitenTitel";
import { HEADER_HEIGHT, SIDEBAR_WIDTH } from "./layoutSizes";

export { HEADER_HEIGHT };

/** Innenabstand der Kopfleiste links und rechts (Toolbar-Gutter) */
const GUTTER = 24;

/** Titel über dem Inhalt: Einblenden beim Seitenwechsel, ohne bei reduzierter Bewegung */
const fadeInSx = {
  "@keyframes header-title-fade-in": {
    from: { opacity: 0 },
    to: { opacity: 1 },
  },
  animation: "header-title-fade-in 0.2s ease 0.1s both",
  "@media (prefers-reduced-motion: reduce)": { animation: "none" },
};

/** Wie das Logo zwischen Mitte und linkem Rand wandert */
const BRAND_MOVE: KeyframeAnimationOptions = {
  duration: 300,
  easing: "cubic-bezier(0.4, 0, 0.2, 1)",
};

const prefersReducedMotion = () =>
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/** Das Logo im Stand und drehend (150 Bilder à 20 ms, eine Runde in 3 s) */
const LOGO_STILL = "/logo.png";
const LOGO_SPINNING = "/logo-small.gif";

/**
 * Nach einer Vierteldrehung steht das Rad im GIF wieder wie im Standbild – nur an diesen
 * Stellen wird zurückgewechselt, sonst spränge der Rotor beim Wegfahren der Maus.
 */
const SPIN_STEP_MS = 750;

/**
 * Lässt das Logo drehen, solange die Maus darüber ist: Standbild gegen das GIF tauschen, das
 * damit von vorn beginnt. Beim Verlassen läuft es bis zur nächsten Vierteldrehung weiter und
 * hält dann. Das GIF wird vorab geladen, damit der erste Hover nicht erst auf den Download
 * wartet. Bei reduzierter Bewegung bleibt das Rad stehen.
 */
const useSpinOnHover = () => {
  const [spinning, setSpinning] = useState(false);
  const startedAt = useRef(0);
  const stopTimer = useRef<number | undefined>(undefined);

  useEffect(() => {
    new Image().src = LOGO_SPINNING;
    return () => window.clearTimeout(stopTimer.current);
  }, []);

  const start = () => {
    window.clearTimeout(stopTimer.current);
    if (spinning || prefersReducedMotion()) return;
    startedAt.current = performance.now();
    setSpinning(true);
  };

  const stop = () => {
    if (!spinning) return;
    const elapsed = performance.now() - startedAt.current;
    stopTimer.current = window.setTimeout(
      () => setSpinning(false),
      SPIN_STEP_MS - (elapsed % SPIN_STEP_MS),
    );
  };

  return { src: spinning ? LOGO_SPINNING : LOGO_STILL, start, stop };
};

/**
 * Lässt ein Element weich an seinen neuen Platz wandern, sobald sich `trigger` ändert (FLIP):
 * alte und neue Lage messen, das Element per `transform` an die alte Stelle zurücksetzen und
 * von dort zur neuen animieren.
 *
 * Über die Web Animations API statt einer JavaScript-Animation: Eine `transform`-Animation
 * läuft im Compositor des Browsers weiter, während der Hauptthread die neue Seite aufbaut
 * (Karte, Tabellen – bis zu 120 ms am Stück).
 *
 * Die alte Lage wird im Render gemessen, in dem sich `trigger` ändert – das DOM zeigt dann
 * noch den bisherigen Stand. Eine früher gemerkte Lage wäre veraltet, sobald sich die Breite
 * ohne neues Rendern ändert (etwa wenn die Markenschrift nachlädt). `getBoundingClientRect`
 * enthält eine laufende Animation; wird mitten in einer Wanderung erneut gewechselt, startet
 * die nächste dort, wo das Element gerade zu sehen ist.
 */
const useSlideOnChange = (
  ref: React.RefObject<HTMLElement | null>,
  trigger: unknown,
) => {
  const lastTrigger = useRef(trigger);
  const fromLeft = useRef<number | null>(null);
  const running = useRef<Animation | null>(null);

  if (lastTrigger.current !== trigger) {
    lastTrigger.current = trigger;
    fromLeft.current = ref.current?.getBoundingClientRect().left ?? null;
  }

  useLayoutEffect(() => {
    const element = ref.current;
    const from = fromLeft.current;
    fromLeft.current = null;
    if (!element || from === null || prefersReducedMotion()) return;

    running.current?.cancel();
    const delta = from - element.getBoundingClientRect().left;
    if (delta === 0) return;
    running.current = element.animate(
      [{ transform: `translateX(${delta}px)` }, { transform: "translateX(0)" }],
      BRAND_MOVE,
    );
  }, [ref, trigger]);
};

interface HeaderProps {
  /** Das geöffnete Projekt; fehlt auf der Startseite, in der Verwaltung und solange es lädt */
  project?: { id: string; name: string };
}

/**
 * Kopfleiste der Anwendung.
 *
 * Das Logo führt immer zur Startseite; beim Hover dreht sich die Mühle und wächst ganz leicht,
 * statt einen Tooltip zu zeigen. Auf der Startseite steht es mit „strømcraft“ in der Mitte; im Projekt und in der
 * Verwaltung wandert es nach links. Die Wanderung übernimmt `useSlideOnChange` – ausgelöst von
 * der Route, nicht vom Laden des Projekts, damit sie mit dem Klick beginnt. Wer im System
 * weniger Bewegung eingestellt hat, sieht keinen Übergang.
 *
 * Außerhalb der Startseite folgt der Header dem Raster darunter: Über der Sidebar stehen Logo
 * und Projektname mit Projektwechsel (bzw. der Mandant, `useHeaderConfig`), über dem Inhalt
 * die Überschrift der Seite (`useHeaderPageHeading`, gesetzt von `PageTemplate`) – außer im
 * Projekt: Dort trägt sie der Seitenkopf (`PageHeader` in `ProjectLayout`).
 *
 * Logo und Projektname gehören dort zur Navigation: Sie bleiben beim Scrollen mit der Sidebar
 * stehen (`position: fixed`), Seitentitel und Einstellungsmenü scrollen mit dem Inhalt weg.
 *
 * Rechts das Einstellungsmenü, absolut gesetzt, damit es die Mitte nicht verschiebt. Einen
 * Zurück-Pfeil gibt es hier nicht – das Logo ersetzt ihn.
 */
const Header: React.FC<HeaderProps> = ({ project }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const { pageTitle, pageHeading, focus } = useHeader();

  const isHome = location.pathname === "/";
  const brandRef = useRef<HTMLDivElement>(null);
  useSlideOnChange(brandRef, isHome);

  // Auf einem Projekt eingeschränkte Startseite: Das Logo hebt die Einschränkung auf, statt
  // auf dieselbe Seite zu führen.
  const goHome = () => (focus ? focus.onClear() : navigate("/"));
  const logo = useSpinOnHover();

  const title = pageTitle ? (
    <Box
      component="span"
      title={pageTitle}
      sx={{
        color: colors.brand.logoText,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}
    >
      {pageTitle}
    </Box>
  ) : project ? (
    <ProjectSwitcher projectId={project.id} projectName={project.name} />
  ) : isHome ? (
    // Nicht per Tastatur erreichbar: Das Logo davor ist derselbe Weg und schon ein Knopf.
    <ButtonBase
      onClick={goHome}
      tabIndex={-1}
      aria-hidden
      sx={{ font: "inherit" }}
    >
      <Box component="span" sx={{ color: colors.brand.logoText }}>
        str
      </Box>
      <Box component="span" sx={{ color: colors.brand.logoAccent }}>
        ø
      </Box>
      <Box component="span" sx={{ color: colors.brand.logoText }}>
        mcraft
      </Box>
    </ButtonBase>
  ) : null;

  // Wechselt der Titel, blendet er neu ein, während das Logo wandert.
  const titleKey = pageTitle ?? project?.id ?? (isHome ? "home" : "loading");

  return (
    <AppBar
      position="static"
      elevation={0}
      sx={{
        backgroundColor: "transparent",
        color: "text.primary",
        width: "100vw",
        overflowX: "hidden",
      }}
    >
      <Toolbar
        sx={{
          height: HEADER_HEIGHT,
          position: "relative",
          justifyContent: isHome ? "center" : "flex-start",
        }}
      >
        <Box
          ref={brandRef}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            minWidth: 0,
            // Über der Sidebar und mit ihr fest: endet an ihrer Kante, der Platzhalter dahinter
            // hält den Seitentitel über dem Inhalt.
            ...(!isHome && {
              position: "fixed",
              top: 0,
              left: GUTTER,
              height: HEADER_HEIGHT,
              width: SIDEBAR_WIDTH - GUTTER,
              zIndex: (theme) => theme.zIndex.appBar,
            }),
          }}
        >
          <ButtonBase
            onClick={goHome}
            aria-label="Startseite"
            onMouseEnter={logo.start}
            onMouseLeave={logo.stop}
            sx={{
              borderRadius: 1,
              flexShrink: 0,
              "& img": { transition: "transform 150ms ease-out" },
              "&:hover img, &:focus-visible img": { transform: "scale(1.04)" },
              "@media (prefers-reduced-motion: reduce)": {
                "& img": { transition: "none" },
              },
            }}
          >
            <Box
              component="img"
              src={logo.src}
              alt="Windprojekt Kalkulator Logo"
              sx={{ height: 35, width: "auto", objectFit: "contain" }}
            />
          </ButtonBase>

          <Box
            key={titleKey}
            sx={{
              display: "flex",
              alignItems: "center",
              minWidth: 0,
              ...fadeInSx,
            }}
          >
            <Typography
              variant="h6"
              component="div"
              sx={{
                display: "flex",
                alignItems: "center",
                minWidth: 0,
                fontWeight: 700,
                fontFamily: fonts.brand,
                fontSize: "1.25rem",
                letterSpacing: "-0.025em",
              }}
            >
              {title}
              {/* Startseite auf ein Projekt eingeschränkt: als Unterebene, nicht als Titel
                    wie im Projekt – man ist noch auf der Startseite */}
              {focus && (
                <>
                  <Box
                    component="span"
                    sx={{
                      color: colors.text.disabled,
                      mx: 1,
                      fontWeight: 400,
                    }}
                  >
                    ›
                  </Box>
                  <Tooltip title="Projekt öffnen">
                    <Box
                      component="span"
                      onClick={focus.onOpen}
                      sx={{
                        // Wie das Logo davor und der Projektname im Projekt: eine Farbe für
                        // die ganze Zeile, die Ebenen trennt das ›
                        color: colors.brand.logoText,
                        cursor: "pointer",
                        "&:hover": { textDecoration: "underline" },
                      }}
                    >
                      {focus.label}
                    </Box>
                  </Tooltip>
                </>
              )}
            </Typography>

            {focus && (
              <Tooltip title="Alle Windparks (Esc)">
                <IconButton
                  onClick={focus.onClear}
                  size="small"
                  sx={{ ml: 1, color: colors.text.secondary }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>

        {!isHome && (
          <Box sx={{ width: SIDEBAR_WIDTH - GUTTER, flexShrink: 0 }} />
        )}

        {!isHome && !project && pageHeading && (
          <Box
            key={pageHeading.titel}
            sx={{
              flex: 1,
              minWidth: 0,
              pl: `${GUTTER}px`,
              // Platz für das Einstellungsmenü rechts
              pr: 8,
              ...fadeInSx,
            }}
          >
            <SeitenTitel back={pageHeading.back} titel={pageHeading.titel} />
          </Box>
        )}

        <Box
          sx={{
            position: "absolute",
            right: GUTTER,
            display: "flex",
            alignItems: "center",
            gap: 2,
          }}
        >
          {isAuthenticated ? (
            <ProfileMenu />
          ) : (
            <Button
              variant="outlined"
              color="inherit"
              sx={{
                borderColor: colors.text.primary,
                color: colors.text.primary,
                "&:hover": {
                  borderColor: colors.primary,
                  backgroundColor: colors.glass.insetSubtle,
                },
              }}
            >
              Anmelden
            </Button>
          )}
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default Header;
